/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import EarningsBar from "@/components/EarningsBar";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Banknote, Plus, Loader2, Trash2, FileText, Download, Send, AlertTriangle,
  CheckCircle2, Clock, Receipt, Mail, Calendar as CalIcon, TrendingUp,
  Users as UsersIcon, Wallet, Bell, Save,
  ChevronRight, ShieldCheck, Scale, Pencil,
} from "lucide-react";import { cn } from "@/lib/utils";
import { AdminBillingContent } from "@/pages/AdminBilling";
import { AdminSepaMandatesContent } from "@/pages/AdminSepaMandates";
import { CustomerRevenueOverview } from "@/components/admin/CustomerRevenueOverview";

const STATUS_META = {
  draft:           { label: "Entwurf",        cls: "bg-zinc-100 text-zinc-700 border-zinc-200",  Icon: FileText },
  sent:            { label: "Gesendet",       cls: "bg-cyan-50 text-cyan-700 border-cyan-200",   Icon: Send },
  partially_paid:  { label: "Teilweise bezahlt", cls: "bg-amber-50 text-amber-800 border-amber-200", Icon: Wallet },
  paid:            { label: "Bezahlt",        cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
  overdue:         { label: "Überfällig",     cls: "bg-rose-50 text-rose-700 border-rose-200",   Icon: AlertTriangle },
};

const EXPENSE_CATEGORY_LABELS = {
  kraftstoff: "Kraftstoff", fahrzeug: "Fahrzeug", buero: "Büro", software: "Software",
  telekom: "Telekom", versicherung: "Versicherung", fortbildung: "Fortbildung",
  marketing: "Marketing", miete: "Miete", steuerberater: "Steuerberater", sonstiges: "Sonstiges",
};

const fmtEuro = (n) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

const fmtDate = (iso) => {
  if (!iso) return "–";
  try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return iso; }
};

export default function Buchhaltung() {
  const { user, refreshMe } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isBuchhalter = user?.role === "backoffice";

  // Read active tab from URL ?tab=…
  const urlTab = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return sp.get("tab") || "overview";
  }, [location.search]);
  const [tab, _setTab] = useState(urlTab);

  // Sync state ↔ URL (pushState so browser back-button cycles tab history)
  const setTab = (next) => {
    _setTab(next);
    const sp = new URLSearchParams(location.search);
    sp.set("tab", next);
    navigate({ pathname: location.pathname, search: sp.toString() });
  };
  useEffect(() => { _setTab(urlTab); }, [urlTab]);

  // FAB action: switch to invoices tab + signal opening of "Neue Rechnung" dialog.
  const [pendingCreate, setPendingCreate] = useState(0);
  const triggerNewInvoice = () => {
    setTab("invoices");
    setPendingCreate((n) => n + 1); // increments → InvoicesTab effect re-fires
  };

  // Build the tab list dynamically (admin gets extra sections)
  const TABS = useMemo(() => {
    if (isBuchhalter) {
      // Backoffice-Sicht: Kunden/Umsatz-Übersicht, Abo-Rechnungen unter
      // „Rechnungen" (inkl. Stripe-Einzug), SEPA-Mandate.
      // DATEV entfernt · Team-Leistung lebt jetzt unter „Benutzer".
      return [
        { key: "overview",      label: "Übersicht",    icon: TrendingUp,  desc: "Kunden · Monatsumsatz" },
        { key: "invoices",      label: "Rechnungen",   icon: FileText,    desc: "Abo-Rechnungen · Stripe-Einzug" },
        { key: "expenses",      label: "Ausgaben",     icon: Receipt,     desc: "Belege, Kategorien" },
        { key: "dunning",       label: "Mahnwesen",    icon: Bell,        desc: "Offene Posten, Mahnstufen" },
        { key: "sepa-mandates", label: "SEPA-Mandate", icon: ShieldCheck, desc: "Online erteilte Mandate" },
        { key: "anwaelte",      label: "Anwälte",      icon: Scale,       desc: "Kanzlei-Stammdaten · Fall-Zähler" },
        { key: "provisionen",   label: "Provisionen",  icon: Wallet,      desc: "Partner-Auszahlungen (200 €/Auftrag)" },
        { key: "settings",      label: "Stammdaten",   icon: Banknote,    desc: "Firmen-, Bank- & Steuerdaten" },
      ];
    }
    return [
      { key: "overview",   label: "Übersicht",      icon: TrendingUp,       desc: "BWA · Einnahmen, Ausgaben, USt." },
      { key: "invoices",   label: "Rechnungen",     icon: FileText,         desc: "Ausgehende Kundenrechnungen" },
      { key: "expenses",   label: "Ausgaben",       icon: Receipt,          desc: "Belege, Kategorien" },
      { key: "dunning",    label: "Mahnwesen",      icon: Bell,             desc: "Offene Posten, Mahnstufen" },
      { key: "settings",   label: "Stammdaten",     icon: Banknote,         desc: "Firmen-, Bank- & Steuerdaten" },
    ];
  }, [isBuchhalter]);

  // Legacy-Deeplinks: alte Tab-Namen auf die neue Struktur mappen
  useEffect(() => {
    if (tab === "subscriptions") setTab("invoices");
    else if (tab === "team" || tab === "datev") setTab("overview");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Subtitle changes per tab
  const activeMeta = TABS.find((t) => t.key === tab) || TABS[0];

  // Buchhalter erstellt keine Endkunden-Rechnungen → kein "Neue Rechnung"-FAB
  const hideFab = isBuchhalter;

  const sidebarExtras = (
    <nav className="flex flex-col gap-1" data-testid="buchhaltung-side-nav">
      {TABS.map(({ key, label, icon: Icon, adminOnly }) => {
        const active = tab === key;
        return (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            data-testid={`side-tab-${key}`}
            className={cn(
              "group relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all",
              "border border-transparent",
              active
                ? "bg-gradient-to-r from-sky-50 to-violet-50 text-sky-900 border-sky-200 shadow-sm"
                : "text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900",
            )}
          >
            <Icon
              className={cn(
                "h-4 w-4 shrink-0 transition-colors",
                active ? "text-sky-700" : "text-zinc-500 group-hover:text-zinc-700",
              )}
            />
            <span className="flex-1 truncate text-left">{label}</span>
            {adminOnly && (
              <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">
                Buchhalter
              </span>
            )}
            {active && <ChevronRight className="h-3.5 w-3.5 text-sky-700 shrink-0" />}
          </button>
        );
      })}
    </nav>
  );

  return (
    <Layout
      title="Buchhaltung"
      subtitle={activeMeta?.desc || "Rechnungen, Ausgaben, Mahnwesen & Steuer-Übersicht"}
      data-testid="buchhaltung-page"
      sidebarExtras={sidebarExtras}
      sidebarExtrasTitle="Buchhaltung"
      primaryAction={hideFab ? null : {
        label: "Neue Rechnung",
        icon: Plus,
        onClick: triggerNewInvoice,
        testid: "mobile-fab-new-invoice",
      }}
    >
      {/* Mobile tab pills — sidebar is hidden on mobile, so show horizontal scroll here */}
      <div className="lg:hidden -mx-5 px-5 mb-4 overflow-x-auto" data-testid="buchhaltung-mobile-tabs">
        <div className="flex items-center gap-1.5 w-max pb-1">
          {TABS.map(({ key, label, icon: Icon }) => {
            const active = tab === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                data-testid={`mobile-tab-${key}`}
                className={cn(
                  "inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-xs font-semibold whitespace-nowrap transition",
                  active
                    ? "bg-gradient-to-r from-sky-500 to-violet-600 text-white shadow-sm"
                    : "bg-white border border-zinc-200 text-zinc-700",
                )}
              >
                <Icon className="h-3.5 w-3.5" />{label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      <div data-testid={`buchhaltung-tab-${tab}`}>
        {tab === "overview" && (isBuchhalter ? <CustomerRevenueOverview /> : <OverviewTab />)}
        {tab === "invoices" && (isBuchhalter
          ? <AdminBillingContent />
          : <InvoicesTab user={user} pendingCreate={pendingCreate} />)}
        {tab === "expenses" && <ExpensesTab />}
        {tab === "dunning" && <DunningTab />}
        {tab === "settings" && <SettingsTab user={user} onSaved={refreshMe} />}
        {tab === "sepa-mandates" && (isBuchhalter
          ? <AdminSepaMandatesContent />
          : <NoAccessNote what="SEPA-Mandate" />)}
        {tab === "anwaelte" && (isBuchhalter
          ? <AnwaelteTab />
          : <NoAccessNote what="Anwälte" />)}
        {tab === "provisionen" && (isBuchhalter
          ? <ProvisionenTab />
          : <NoAccessNote what="Provisionen" />)}
      </div>
    </Layout>
  );
}

function NoAccessNote({ what }) {
  return (
    <div className="rounded-2xl border-2 border-dashed border-zinc-200 bg-white px-6 py-16 text-center" data-testid="buchhaltung-no-access">
      <div className="text-zinc-400 text-5xl mb-3">🔒</div>
      <div className="font-display text-xl font-bold text-zinc-900">Kein Zugriff auf "{what}"</div>
      <p className="text-sm text-zinc-500 mt-2 max-w-md mx-auto">
        Dieser Bereich ist nur für die Buchhaltung (Backoffice) sichtbar. Wechsle in der linken Spalte
        auf einen anderen Reiter.
      </p>
    </div>
  );
}

/* ─────────────────────────  ÜBERSICHT (BWA)  ───────────────────────── */
function OverviewTab() {
  const [period, setPeriod] = useState("year");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/accounting/summary?period=${period}`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [period]);

  if (loading || !data) return <Loader />;
  const max = Math.max(...data.monthly.map((m) => Math.max(m.revenue, m.expenses)), 1);

  return (
    <div className="space-y-6" data-testid="overview-tab">
      {/* Mobile: Verdienst-Karte im nativen App-Look */}
      <div className="lg:hidden"><EarningsBar /></div>
      <div className="flex items-center gap-3">
        <span className="text-sm text-zinc-600">Zeitraum:</span>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[180px]" data-testid="overview-period">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="month">Aktueller Monat</SelectItem>
            <SelectItem value="quarter">Aktuelles Quartal</SelectItem>
            <SelectItem value="year">Aktuelles Jahr</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-zinc-400">{data.from} – {data.to}</span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Einnahmen brutto" value={fmtEuro(data.revenue_gross)} note={`${data.invoice_count} Rechnungen`} color="emerald" />
        <KpiCard label="Ausgaben brutto" value={fmtEuro(data.expenses_gross)} note={`${data.expense_count} Belege`} color="rose" />
        <KpiCard label="Überschuss netto" value={fmtEuro(data.profit_net)} note="Einnahmen − Ausgaben (netto)" color={data.profit_net >= 0 ? "emerald" : "rose"} />
        <KpiCard label="USt-Vorauszahlung" value={fmtEuro(data.vat_due)} note={`Eingenommen ${fmtEuro(data.vat_collected)} − Vorsteuer ${fmtEuro(data.vat_paid_input)}`} color="cyan" />
      </div>

      {/* Monthly bar chart (CSS only — keine extra Library) */}
      <Card className="p-5" data-testid="overview-trend">
        <div className="font-display font-bold text-lg mb-4">Monatlicher Verlauf</div>
        {data.monthly.length === 0 ? (
          <div className="text-sm text-zinc-400 py-6 text-center">Keine Buchungen im gewählten Zeitraum.</div>
        ) : (
          <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${Math.min(data.monthly.length, 12)}, minmax(0, 1fr))` }}>
            {data.monthly.map((m) => (
              <div key={m.month} className="flex flex-col items-center gap-1 group">
                <div className="flex items-end gap-1 h-32 w-full">
                  <div
                    className="flex-1 bg-emerald-500 rounded-t transition-all hover:bg-emerald-600"
                    style={{ height: `${(m.revenue / max) * 100}%` }}
                    title={`Einnahmen: ${fmtEuro(m.revenue)}`}
                  />
                  <div
                    className="flex-1 bg-rose-400 rounded-t transition-all hover:bg-rose-500"
                    style={{ height: `${(m.expenses / max) * 100}%` }}
                    title={`Ausgaben: ${fmtEuro(m.expenses)}`}
                  />
                </div>
                <span className="text-[10px] text-zinc-500 font-mono">{m.month.slice(5)}</span>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center gap-4 mt-4 text-xs text-zinc-500">
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-emerald-500" /> Einnahmen</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-rose-400" /> Ausgaben</span>
        </div>
      </Card>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5" data-testid="overview-top-customers">
          <div className="flex items-center gap-2 mb-3">
            <UsersIcon className="h-4 w-4 text-zinc-500" />
            <div className="font-display font-bold">Top-Kunden</div>
          </div>
          {data.top_customers.length === 0 ? (
            <div className="text-sm text-zinc-400 py-4 text-center">Noch keine Daten</div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {data.top_customers.map((c, i) => (
                <li key={c.name} className="flex items-center justify-between py-2.5 text-sm">
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="h-6 w-6 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
                    <span className="truncate">{c.name}</span>
                  </span>
                  <span className="font-mono font-semibold text-zinc-900">{fmtEuro(c.amount)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card className="p-5" data-testid="overview-top-categories">
          <div className="flex items-center gap-2 mb-3">
            <Receipt className="h-4 w-4 text-zinc-500" />
            <div className="font-display font-bold">Top-Ausgaben-Kategorien</div>
          </div>
          {data.top_categories.length === 0 ? (
            <div className="text-sm text-zinc-400 py-4 text-center">Noch keine Daten</div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {data.top_categories.map((c, i) => (
                <li key={c.category} className="flex items-center justify-between py-2.5 text-sm">
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="h-6 w-6 rounded-full bg-rose-50 text-rose-700 text-[10px] font-bold flex items-center justify-center">{i + 1}</span>
                    <span className="truncate">{EXPENSE_CATEGORY_LABELS[c.category] || c.category}</span>
                  </span>
                  <span className="font-mono font-semibold text-zinc-900">{fmtEuro(c.amount)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

function KpiCard({ label, value, note, color }) {
  const palette = {
    emerald: "bg-emerald-50 text-emerald-700",
    rose:    "bg-rose-50 text-rose-700",
    cyan:    "bg-cyan-50 text-cyan-700",
    violet:  "bg-violet-50 text-violet-700",
  };
  return (
    <Card className="p-5">
      <div className={cn("inline-flex h-7 px-2 items-center rounded-full text-[10px] uppercase tracking-wider font-bold", palette[color] || palette.emerald)}>
        {label}
      </div>
      <div className="font-display text-2xl lg:text-3xl font-bold mt-3 tracking-tight">{value}</div>
      <div className="text-[11px] text-zinc-500 mt-1">{note}</div>
    </Card>
  );
}

/* ─────────────────────────  RECHNUNGEN  ───────────────────────── */
const emptyItem = () => ({ description: "", quantity: 1, unit_price: 0, tax_rate: 19 });
const emptyInvoice = (klein = false) => ({
  customer_name: "", customer_address: "", customer_email: "",
  case_id: null, case_number: "", issue_date: "", due_date: "",
  items: [emptyItem()],
  notes: "", payment_terms: "", kleinunternehmer: klein,
});

function InvoicesTab({ user, pendingCreate }) {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyInvoice());
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: inv }, { data: st }] = await Promise.all([
        api.get("/invoices"),
        api.get("/invoices/stats"),
      ]);
      setItems(inv.items || []);
      setStats(st);
    } catch (e) {
      toast.error(apiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => items.filter((i) => (filter === "all" ? true : i.status === filter)),
    [items, filter]
  );

  const openCreate = () => {
    setEditing(null);
    setForm(emptyInvoice(user?.kleinunternehmer));
    setOpen(true);
  };
  // External trigger from FAB / mobile bottom-bar — `pendingCreate` is a
  // monotonically increasing counter; every change opens the dialog once.
  useEffect(() => {
    if (pendingCreate && pendingCreate > 0) openCreate();
    // eslint-disable-next-line
  }, [pendingCreate]);
  const openEdit = (inv) => {
    setEditing(inv);
    setForm({
      ...emptyInvoice(),
      ...inv,
      items: inv.items?.length ? inv.items : [emptyItem()],
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.customer_name) return toast.error("Kunde ist Pflichtfeld");
    if (!form.items?.length) return toast.error("Mindestens eine Position");
    setSaving(true);
    try {
      const payload = {
        ...form,
        items: form.items.map((it) => ({
          description: it.description || "",
          quantity: Number(it.quantity) || 0,
          unit_price: Number(it.unit_price) || 0,
          tax_rate: Number(it.tax_rate) || 0,
        })),
      };
      if (editing) await api.patch(`/invoices/${editing.id}`, payload);
      else await api.post("/invoices", payload);
      toast.success(editing ? "Rechnung aktualisiert" : "Rechnung angelegt");
      setOpen(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (inv) => {
    if (!window.confirm(`Rechnung ${inv.invoice_number} löschen?`)) return;
    try {
      await api.delete(`/invoices/${inv.id}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const markSent = async (inv) => {
    try { await api.post(`/invoices/${inv.id}/send`); toast.success("Als gesendet markiert"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const downloadPdf = async (inv) => {
    setDownloading(inv.id);
    try {
      const r = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `Rechnung-${inv.invoice_number}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error(apiError(e, "PDF-Export fehlgeschlagen")); }
    finally { setDownloading(null); }
  };

  return (
    <div className="space-y-5" data-testid="invoices-tab">
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard label="Umsatz gesamt" value={fmtEuro(stats.total_amount)} note={`${stats.count} Rechnungen`} color="cyan" />
          <KpiCard label="Bezahlt" value={fmtEuro(stats.paid_amount)} note={`${stats.by_status.paid || 0} bezahlt`} color="emerald" />
          <KpiCard label="Offen" value={fmtEuro(stats.open_amount)} note={`${(stats.by_status.sent || 0) + (stats.by_status.partially_paid || 0)} offen`} color="violet" />
          <KpiCard label="Überfällig" value={`${stats.by_status.overdue || 0}`} note="Rechnungen über Fälligkeit" color="rose" />
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-lg border border-zinc-200 bg-white p-0.5">
          {[
            { v: "all", l: "Alle" },
            { v: "draft", l: "Entwurf" },
            { v: "sent", l: "Offen" },
            { v: "partially_paid", l: "Teilweise" },
            { v: "paid", l: "Bezahlt" },
            { v: "overdue", l: "Überfällig" },
          ].map((b) => (
            <button
              key={b.v}
              onClick={() => setFilter(b.v)}
              className={cn("px-3 py-1.5 rounded-md text-sm font-medium transition",
                filter === b.v ? "bg-emerald-50 text-emerald-700" : "text-zinc-600 hover:text-zinc-900")}
              data-testid={`invoice-filter-${b.v}`}
            >
              {b.l}
            </button>
          ))}
        </div>
        <Button onClick={openCreate} className="brand-gradient-bg text-white" data-testid="invoice-create-btn">
          <Plus className="h-4 w-4 mr-2" /> Neue Rechnung
        </Button>
      </div>

      {loading ? <Loader /> : filtered.length === 0 ? (
        <Card className="p-12 text-center text-zinc-500" data-testid="invoices-empty">Keine Rechnungen.</Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="invoices-table">
              <thead className="bg-zinc-50/80 border-b border-zinc-100">
                <tr className="text-left">
                  <Th>Rechnung</Th>
                  <Th>Kunde</Th>
                  <Th>Datum</Th>
                  <Th>Fällig</Th>
                  <Th className="text-right">Brutto</Th>
                  <Th className="text-right">Bezahlt</Th>
                  <Th>Status</Th>
                  <Th className="text-right">Aktionen</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {filtered.map((inv) => {
                  const meta = STATUS_META[inv.status] || STATUS_META.draft;
                  return (
                    <tr key={inv.id} className="hover:bg-zinc-50/70 transition cursor-pointer" onClick={() => openEdit(inv)}
                        data-testid={`invoice-row-${inv.invoice_number}`}>
                      <td className="px-4 py-3 font-mono text-[12px] font-semibold">{inv.invoice_number}</td>
                      <td className="px-4 py-3 truncate max-w-[240px]">
                        <div className="font-display font-semibold">{inv.customer_name}</div>
                        {inv.case_number && <div className="text-[11px] text-zinc-400">Akte {inv.case_number}</div>}
                      </td>
                      <td className="px-4 py-3 text-zinc-700">{fmtDate(inv.issue_date)}</td>
                      <td className="px-4 py-3 text-zinc-700">{fmtDate(inv.due_date)}</td>
                      <td className="px-4 py-3 text-right font-mono">{fmtEuro(inv.total_gross || 0)}</td>
                      <td className="px-4 py-3 text-right font-mono text-emerald-700">{fmtEuro(inv.paid_amount || 0)}</td>
                      <td className="px-4 py-3">
                        <span className={cn("inline-flex items-center gap-1 text-[11px] font-semibold border rounded-full px-2 py-0.5", meta.cls)}>
                          <meta.Icon className="h-3 w-3" /> {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="inline-flex gap-0.5">
                          <Button size="icon" variant="ghost" className="h-8 w-8" title="PDF" onClick={() => downloadPdf(inv)} data-testid={`invoice-pdf-${inv.invoice_number}`}>
                            {downloading === inv.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                          </Button>
                          {inv.status === "draft" && (
                            <Button size="icon" variant="ghost" className="h-8 w-8" title="Als gesendet markieren" onClick={() => markSent(inv)} data-testid={`invoice-send-${inv.invoice_number}`}>
                              <Send className="h-4 w-4" />
                            </Button>
                          )}
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" title="Löschen" onClick={() => remove(inv)} data-testid={`invoice-del-${inv.invoice_number}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <InvoiceDialog open={open} onClose={() => setOpen(false)} form={form} setForm={setForm} editing={editing} saving={saving} onSave={save} onReload={load} user={user} />
    </div>
  );
}

function InvoiceDialog({ open, onClose, form, setForm, editing, saving, onSave, onReload, user }) {
  const klein = !!form.kleinunternehmer;
  const totals = useMemo(() => {
    const items = form.items || [];
    let net = 0, vat = 0;
    items.forEach((it) => {
      const ln = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
      net += ln;
      if (!klein) vat += ln * (Number(it.tax_rate) || 0) / 100;
    });
    return { net, vat, gross: net + vat };
  }, [form.items, klein]);

  const updateItem = (idx, patch) => {
    setForm((f) => {
      const items = [...(f.items || [])];
      items[idx] = { ...items[idx], ...patch };
      return { ...f, items };
    });
  };
  const addItem = () => setForm((f) => ({ ...f, items: [...(f.items || []), emptyItem()] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="invoice-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? `Rechnung ${editing.invoice_number}` : "Neue Rechnung"}</DialogTitle>
          <DialogDescription>Positions-Editor mit USt-Berechnung & Zahlungseingänge</DialogDescription>
        </DialogHeader>

        <div className="grid lg:grid-cols-2 gap-4">
          <Field label="Kunde *">
            <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="invoice-form-customer" />
          </Field>
          <Field label="E-Mail">
            <Input type="email" value={form.customer_email || ""} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} data-testid="invoice-form-email" />
          </Field>
          <Field label="Adresse">
            <Textarea rows={2} value={form.customer_address || ""} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} data-testid="invoice-form-address" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Rechnungsdatum">
              <Input type="date" value={form.issue_date || ""} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} data-testid="invoice-form-issue" />
            </Field>
            <Field label="Fällig am">
              <Input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="invoice-form-due" />
            </Field>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 mt-2">
          <Label className="flex items-center gap-2 cursor-pointer">
            <Switch checked={klein} onCheckedChange={(v) => setForm({ ...form, kleinunternehmer: v })} data-testid="invoice-klein-toggle" />
            <span className="text-sm">Kleinunternehmer §19 UStG (keine USt)</span>
          </Label>
        </div>

        {/* Items */}
        <div className="mt-3 border border-zinc-200 rounded-lg overflow-hidden" data-testid="invoice-items-section">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-zinc-50 text-[10px] uppercase tracking-wider font-bold text-zinc-500">
            <div className="col-span-6">Bezeichnung</div>
            <div className="col-span-1 text-right">Menge</div>
            <div className="col-span-2 text-right">Einzelpreis</div>
            {!klein && <div className="col-span-1 text-right">USt %</div>}
            <div className={cn("text-right", klein ? "col-span-2" : "col-span-1")}>Summe</div>
            <div className="col-span-1"></div>
          </div>
          {(form.items || []).map((it, idx) => {
            const line = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
            return (
              <div key={idx} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-zinc-100" data-testid={`invoice-item-${idx}`}>
                <Input className="col-span-6 h-8" placeholder="Leistung..." value={it.description}
                       onChange={(e) => updateItem(idx, { description: e.target.value })} data-testid={`invoice-item-desc-${idx}`} />
                <Input className="col-span-1 h-8 text-right" type="number" step="0.01" value={it.quantity}
                       onChange={(e) => updateItem(idx, { quantity: e.target.value })} data-testid={`invoice-item-qty-${idx}`} />
                <Input className="col-span-2 h-8 text-right" type="number" step="0.01" value={it.unit_price}
                       onChange={(e) => updateItem(idx, { unit_price: e.target.value })} data-testid={`invoice-item-price-${idx}`} />
                {!klein && (
                  <Select value={String(it.tax_rate)} onValueChange={(v) => updateItem(idx, { tax_rate: Number(v) })}>
                    <SelectTrigger className="col-span-1 h-8" data-testid={`invoice-item-tax-${idx}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="19">19 %</SelectItem>
                      <SelectItem value="7">7 %</SelectItem>
                      <SelectItem value="0">0 %</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                <div className={cn("text-right text-sm font-mono pt-1.5", klein ? "col-span-2" : "col-span-1")}>{fmtEuro(line)}</div>
                <button onClick={() => removeItem(idx)} className="col-span-1 text-rose-500 hover:bg-rose-50 rounded p-1" data-testid={`invoice-item-del-${idx}`}>
                  <Trash2 className="h-3.5 w-3.5 mx-auto" />
                </button>
              </div>
            );
          })}
          <button onClick={addItem} className="w-full px-3 py-2 text-sm text-emerald-700 hover:bg-emerald-50 border-t border-zinc-100" data-testid="invoice-item-add">
            <Plus className="h-3.5 w-3.5 inline mr-1" /> Position hinzufügen
          </button>
        </div>

        {/* Totals */}
        <div className="flex justify-end mt-3">
          <div className="text-right space-y-0.5 text-sm tabular-nums">
            <div className="text-zinc-500">Netto-Summe: <span className="font-mono font-semibold ml-2">{fmtEuro(totals.net)}</span></div>
            {!klein && <div className="text-zinc-500">USt: <span className="font-mono font-semibold ml-2">{fmtEuro(totals.vat)}</span></div>}
            <div className="text-base font-display font-bold text-emerald-700 mt-0.5">Gesamt: <span className="ml-2">{fmtEuro(totals.gross)}</span></div>
          </div>
        </div>

        <Field label="Notizen (erscheinen im PDF)">
          <Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="invoice-form-notes" />
        </Field>

        {/* Existing payments (if editing) */}
        {editing && <PaymentSection invoice={editing} onReload={onReload} />}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button className="brand-gradient-bg text-white" disabled={saving} onClick={onSave} data-testid="invoice-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PaymentSection({ invoice, onReload }) {
  const [adding, setAdding] = useState(false);
  const [pay, setPay] = useState({ paid_at: new Date().toISOString().slice(0, 10), amount: 0, method: "transfer", note: "" });
  const [saving, setSaving] = useState(false);
  const [list, setList] = useState(invoice.payments || []);
  const [total, setTotal] = useState({ paid: invoice.paid_amount || 0, remaining: invoice.remaining_amount || 0 });

  const add = async () => {
    if (!pay.amount || Number(pay.amount) <= 0) return toast.error("Betrag eingeben");
    setSaving(true);
    try {
      const { data } = await api.post(`/invoices/${invoice.id}/payments`, { ...pay, amount: Number(pay.amount) });
      setList(data.invoice.payments || []);
      setTotal({ paid: data.invoice.paid_amount, remaining: data.invoice.remaining_amount });
      toast.success(`Zahlung verbucht`);
      setAdding(false);
      setPay({ paid_at: new Date().toISOString().slice(0, 10), amount: 0, method: "transfer", note: "" });
      onReload?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  const remove = async (pid) => {
    try {
      const { data } = await api.delete(`/invoices/${invoice.id}/payments/${pid}`);
      setList(data.invoice.payments || []);
      setTotal({ paid: data.invoice.paid_amount, remaining: data.invoice.remaining_amount });
      onReload?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="mt-4 border border-zinc-200 rounded-lg p-4 bg-zinc-50/40" data-testid="invoice-payments">
      <div className="flex items-center justify-between mb-2">
        <div className="font-display font-bold text-sm">Zahlungseingänge</div>
        <div className="text-xs text-zinc-500">
          Bezahlt <strong className="text-emerald-700">{fmtEuro(total.paid)}</strong> · Offen <strong className="text-rose-700">{fmtEuro(total.remaining)}</strong>
        </div>
      </div>
      {list.length > 0 && (
        <ul className="divide-y divide-zinc-100 mb-3 bg-white rounded-lg border border-zinc-100">
          {list.map((p) => (
            <li key={p.id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`payment-${p.id}`}>
              <div>
                <div className="font-mono">{fmtEuro(p.amount)}</div>
                <div className="text-[10px] text-zinc-400">{fmtDate(p.paid_at)} · {p.method}{p.note ? ` · ${p.note}` : ""}</div>
              </div>
              <button onClick={() => remove(p.id)} className="text-rose-500 hover:bg-rose-50 rounded p-1" data-testid={`payment-del-${p.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {adding ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          <Input type="date" value={pay.paid_at} onChange={(e) => setPay({ ...pay, paid_at: e.target.value })} data-testid="payment-date" />
          <Input type="number" step="0.01" placeholder="Betrag" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} data-testid="payment-amount" />
          <Select value={pay.method} onValueChange={(v) => setPay({ ...pay, method: v })}>
            <SelectTrigger data-testid="payment-method"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="transfer">Überweisung</SelectItem>
              <SelectItem value="cash">Bar</SelectItem>
              <SelectItem value="card">Karte</SelectItem>
              <SelectItem value="other">Sonstiges</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex gap-1">
            <Button onClick={add} disabled={saving} size="sm" className="flex-1 brand-gradient-bg text-white" data-testid="payment-save">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Speichern"}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setAdding(false)}>Abbrechen</Button>
          </div>
        </div>
      ) : (
        <Button size="sm" variant="outline" onClick={() => setAdding(true)} data-testid="payment-add">
          <Plus className="h-3.5 w-3.5 mr-1" /> Zahlungseingang erfassen
        </Button>
      )}
    </div>
  );
}

/* ─────────────────────────  AUSGABEN  ───────────────────────── */
const emptyExpense = () => ({
  expense_date: new Date().toISOString().slice(0, 10),
  vendor: "", category: "sonstiges", description: "",
  amount_net: 0, tax_rate: 19, amount_gross: 0,
  invoice_number: "", payment_method: "transfer",
});

function ExpensesTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyExpense());
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/expenses");
      setList(data.items || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.vendor) return toast.error("Lieferant ist Pflichtfeld");
    setSaving(true);
    try {
      const payload = { ...form, amount_net: Number(form.amount_net), tax_rate: Number(form.tax_rate), amount_gross: Number(form.amount_gross) };
      if (editing) await api.patch(`/expenses/${editing.id}`, payload);
      else await api.post("/expenses", payload);
      toast.success(editing ? "Beleg aktualisiert" : "Beleg gespeichert");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  const remove = async (e) => {
    if (!window.confirm(`Beleg löschen?`)) return;
    try { await api.delete(`/expenses/${e.id}`); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const total = list.reduce((s, e) => s + (Number(e.amount_gross) || 0), 0);

  return (
    <div className="space-y-4" data-testid="expenses-tab">
      <div className="flex items-center justify-between">
        <div className="text-sm text-zinc-500">{list.length} Belege · Summe brutto <strong className="text-zinc-900">{fmtEuro(total)}</strong></div>
        <Button onClick={() => { setEditing(null); setForm(emptyExpense()); setOpen(true); }} className="brand-gradient-bg text-white" data-testid="expense-create-btn">
          <Plus className="h-4 w-4 mr-2" /> Beleg erfassen
        </Button>
      </div>
      {loading ? <Loader /> : list.length === 0 ? (
        <Card className="p-12 text-center text-zinc-500" data-testid="expenses-empty">Noch keine Belege erfasst.</Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50/80 border-b border-zinc-100">
                <tr className="text-left">
                  <Th>Datum</Th><Th>Lieferant</Th><Th>Kategorie</Th>
                  <Th className="text-right">Netto</Th><Th className="text-right">USt</Th><Th className="text-right">Brutto</Th>
                  <Th className="text-right">Aktionen</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {list.map((e) => (
                  <tr key={e.id} className="hover:bg-zinc-50/70 transition cursor-pointer" onClick={() => { setEditing(e); setForm({ ...emptyExpense(), ...e }); setOpen(true); }}
                      data-testid={`expense-row-${e.id}`}>
                    <td className="px-4 py-3">{fmtDate(e.expense_date)}</td>
                    <td className="px-4 py-3 font-display font-semibold">{e.vendor}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className="text-[10px]">{EXPENSE_CATEGORY_LABELS[e.category] || e.category}</Badge>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-zinc-700">{fmtEuro(e.amount_net)}</td>
                    <td className="px-4 py-3 text-right text-zinc-500">{e.tax_rate}%</td>
                    <td className="px-4 py-3 text-right font-mono font-semibold">{fmtEuro(e.amount_gross)}</td>
                    <td className="px-4 py-3 text-right" onClick={(ev) => ev.stopPropagation()}>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" onClick={() => remove(e)} data-testid={`expense-del-${e.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <ExpenseDialog open={open} onClose={() => setOpen(false)} form={form} setForm={setForm} editing={editing} saving={saving} onSave={save} />
    </div>
  );
}

function ExpenseDialog({ open, onClose, form, setForm, editing, saving, onSave }) {
  // Auto-update gross when net or tax changes (until user types gross manually).
  useEffect(() => {
    const net = Number(form.amount_net) || 0;
    const rate = Number(form.tax_rate) || 0;
    setForm((f) => ({ ...f, amount_gross: Math.round(net * (1 + rate / 100) * 100) / 100 }));
    // eslint-disable-next-line
  }, [form.amount_net, form.tax_rate]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="expense-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Beleg bearbeiten" : "Beleg erfassen"}</DialogTitle>
          <DialogDescription>Eingangsrechnung / Quittung erfassen — wird in der BWA berücksichtigt.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Datum *">
            <Input type="date" value={form.expense_date || ""} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} data-testid="expense-date" />
          </Field>
          <Field label="Kategorie">
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="expense-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(EXPENSE_CATEGORY_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Lieferant *">
            <Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} data-testid="expense-vendor" />
          </Field>
          <Field label="Beleg-/Rechnungsnummer">
            <Input value={form.invoice_number || ""} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} data-testid="expense-invnum" />
          </Field>
          <Field label="Beschreibung">
            <Input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="expense-desc" />
          </Field>
          <Field label="Zahlungsmittel">
            <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
              <SelectTrigger data-testid="expense-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="transfer">Überweisung</SelectItem>
                <SelectItem value="cash">Bar</SelectItem>
                <SelectItem value="card">Karte</SelectItem>
                <SelectItem value="paypal">PayPal</SelectItem>
                <SelectItem value="other">Sonstiges</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Netto-Betrag">
            <Input type="number" step="0.01" value={form.amount_net} onChange={(e) => setForm({ ...form, amount_net: e.target.value })} data-testid="expense-net" />
          </Field>
          <Field label="USt %">
            <Select value={String(form.tax_rate)} onValueChange={(v) => setForm({ ...form, tax_rate: Number(v) })}>
              <SelectTrigger data-testid="expense-tax"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="19">19 %</SelectItem>
                <SelectItem value="7">7 %</SelectItem>
                <SelectItem value="0">0 %</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Brutto-Betrag (auto)">
            <Input type="number" step="0.01" value={form.amount_gross} onChange={(e) => setForm({ ...form, amount_gross: e.target.value })} data-testid="expense-gross" />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button className="brand-gradient-bg text-white" disabled={saving} onClick={onSave} data-testid="expense-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────────  MAHNWESEN  ───────────────────────── */
function DunningTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const { user } = useAuth();
  const fees = user?.default_dunning_fees || [5, 10, 15];

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/invoices?overdue_only=true");
      setItems(data.items || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const issueDunning = async (inv) => {
    const nextLevel = Math.min(3, (inv.dunning_level || 0) + 1);
    const fee = fees[nextLevel - 1] || 0;
    if (!window.confirm(`${nextLevel}. Mahnung mit ${fmtEuro(fee)} Mahngebühr erstellen?`)) return;
    setBusy(inv.id);
    try {
      await api.post(`/invoices/${inv.id}/dunning`, { level: nextLevel, fee, note: `Mahnung Stufe ${nextLevel}` });
      toast.success(`${nextLevel}. Mahnung erstellt`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-4" data-testid="dunning-tab">
      <Card className="p-5 bg-amber-50/30 border-amber-100">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div>
            <div className="font-display font-bold">Überfällige Rechnungen</div>
            <div className="text-sm text-zinc-600">Mahngebühren laut Stammdaten: <strong>{fees.map(fmtEuro).join(" / ")}</strong> (1./2./3. Mahnung)</div>
          </div>
        </div>
      </Card>
      {loading ? <Loader /> : items.length === 0 ? (
        <Card className="p-12 text-center text-zinc-500" data-testid="dunning-empty">
          <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
          Alles sauber — keine überfälligen Rechnungen.
        </Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50/80 border-b border-zinc-100">
              <tr className="text-left">
                <Th>Rechnung</Th><Th>Kunde</Th><Th>Fällig</Th>
                <Th className="text-right">Offen</Th><Th>Mahnstufe</Th><Th className="text-right">Aktion</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {items.map((inv) => {
                const overdueDays = Math.max(0, Math.floor((Date.now() - new Date(inv.due_date).getTime()) / 86400000));
                const next = Math.min(3, (inv.dunning_level || 0) + 1);
                return (
                  <tr key={inv.id} data-testid={`dunning-row-${inv.invoice_number}`}>
                    <td className="px-4 py-3 font-mono text-[12px] font-semibold">{inv.invoice_number}</td>
                    <td className="px-4 py-3">{inv.customer_name}</td>
                    <td className="px-4 py-3 text-rose-700">{fmtDate(inv.due_date)} <span className="text-[10px] text-rose-500">({overdueDays}d überfällig)</span></td>
                    <td className="px-4 py-3 text-right font-mono">{fmtEuro(inv.remaining_amount)}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={inv.dunning_level >= 3 ? "border-rose-300 text-rose-700" : "border-amber-300 text-amber-800"}>
                        {inv.dunning_level || 0} / 3
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {inv.dunning_level >= 3 ? (
                        <span className="text-xs text-zinc-400">Inkasso prüfen</span>
                      ) : (
                        <Button size="sm" variant="outline" disabled={busy === inv.id} onClick={() => issueDunning(inv)} data-testid={`dunning-issue-${inv.invoice_number}`}>
                          {busy === inv.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5 mr-1" />}
                          {next}. Mahnung
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

/* ─────────────────────────  STAMMDATEN  ───────────────────────── */
function SettingsTab({ user, onSaved }) {
  const [data, setData] = useState({
    iban: user?.iban || "", bic: user?.bic || "", bank_name: user?.bank_name || "",
    tax_number: user?.tax_number || "", vat_id: user?.vat_id || "",
    kleinunternehmer: !!user?.kleinunternehmer,
    default_payment_days: user?.default_payment_days ?? 14,
    default_dunning_fees: user?.default_dunning_fees || [5, 10, 15],
    default_payment_terms: user?.default_payment_terms || "Bitte überweisen Sie den Betrag bis zum Fälligkeitsdatum auf das untenstehende Konto.",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me/profile", data);
      toast.success("Stammdaten gespeichert");
      onSaved?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="settings-tab">
      <Card className="p-5">
        <div className="font-display font-bold mb-4">Steuer-Modus</div>
        <Label className="flex items-center gap-3 cursor-pointer">
          <Switch checked={data.kleinunternehmer} onCheckedChange={(v) => setData({ ...data, kleinunternehmer: v })} data-testid="settings-klein" />
          <div>
            <div className="font-medium">Kleinunternehmer §19 UStG</div>
            <div className="text-xs text-zinc-500">Wenn aktiv: Rechnungen werden ohne USt ausgewiesen, mit Hinweis-Text.</div>
          </div>
        </Label>
      </Card>

      <Card className="p-5">
        <div className="font-display font-bold mb-4">Bankverbindung & Steuer</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="IBAN"><Input value={data.iban} onChange={(e) => setData({ ...data, iban: e.target.value })} data-testid="settings-iban" /></Field>
          <Field label="BIC"><Input value={data.bic} onChange={(e) => setData({ ...data, bic: e.target.value })} data-testid="settings-bic" /></Field>
          <Field label="Bank"><Input value={data.bank_name} onChange={(e) => setData({ ...data, bank_name: e.target.value })} data-testid="settings-bank" /></Field>
          <Field label="Steuernummer"><Input value={data.tax_number} onChange={(e) => setData({ ...data, tax_number: e.target.value })} data-testid="settings-taxnr" /></Field>
          <Field label="USt-IdNr."><Input value={data.vat_id} onChange={(e) => setData({ ...data, vat_id: e.target.value })} data-testid="settings-vatid" /></Field>
        </div>
      </Card>

      <Card className="p-5">
        <div className="font-display font-bold mb-4">Standard-Zahlungsbedingungen</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Standard-Zahlungsziel (Tage)">
            <Input type="number" value={data.default_payment_days} onChange={(e) => setData({ ...data, default_payment_days: Number(e.target.value) })} data-testid="settings-payment-days" />
          </Field>
        </div>
        <Field label="Hinweistext (auf Rechnung)" className="mt-3">
          <Textarea rows={2} value={data.default_payment_terms} onChange={(e) => setData({ ...data, default_payment_terms: e.target.value })} data-testid="settings-payment-terms" />
        </Field>
      </Card>

      <Card className="p-5">
        <div className="font-display font-bold mb-4">Mahngebühren</div>
        <div className="grid grid-cols-3 gap-3">
          {[0, 1, 2].map((i) => (
            <Field key={i} label={`${i + 1}. Mahnung (€)`}>
              <Input type="number" step="0.01" value={data.default_dunning_fees[i]} onChange={(e) => {
                const arr = [...data.default_dunning_fees];
                arr[i] = Number(e.target.value);
                setData({ ...data, default_dunning_fees: arr });
              }} data-testid={`settings-dunning-${i + 1}`} />
            </Field>
          ))}
        </div>
      </Card>

      <Button className="brand-gradient-bg text-white" disabled={saving} onClick={save} data-testid="settings-save">
        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
        Stammdaten speichern
      </Button>
    </div>
  );
}

/* ─────────────────────────  Helpers  ───────────────────────── */
function Th({ children, className }) {
  return <th className={cn("px-4 py-3 text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500", className)}>{children}</th>;
}
function Field({ label, children, className }) {
  return (
    <div className={className}>
      <Label className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-500">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
function Loader() {
  return <div className="flex items-center justify-center py-12 text-zinc-500"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade…</div>;
}

/* ============ ANWÄLTE (Stammdaten + Vollmacht-Zähler) ============ */
const emptyLawyer = () => ({ name: "", firm: "", address_line: "", postal_code: "", city: "", phone: "", email: "" });

function AnwaelteTab() {
  const [stats, setStats] = useState([]);
  const [lawyers, setLawyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyLawyer());
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: l }] = await Promise.all([
        api.get("/lawyers/stats"),
        api.get("/lawyers"),
      ]);
      setStats(s.stats || []);
      setLawyers(l.lawyers || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Bitte Name des Anwalts / Ansprechpartners angeben.");
      return;
    }
    setBusy(true);
    try {
      if (editingId) {
        await api.patch(`/lawyers/${editingId}`, form);
        toast.success("Anwalt aktualisiert");
      } else {
        await api.post("/lawyers", form);
        toast.success("Anwalt angelegt");
      }
      setForm(emptyLawyer());
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (l) => {
    setEditingId(l.id);
    setForm({
      name: l.name || "", firm: l.firm || "", address_line: l.address_line || "",
      postal_code: l.postal_code || "", city: l.city || "", phone: l.phone || "", email: l.email || "",
    });
  };

  const remove = async (id) => {
    if (!window.confirm("Diesen Anwalt wirklich löschen? Bereits zugewiesene Akten behalten die Kanzlei-Daten.")) return;
    try {
      await api.delete(`/lawyers/${id}`);
      toast.success("Anwalt gelöscht");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (loading) return <Loader />;

  const statById = Object.fromEntries(stats.map((s) => [s.lawyer_id, s]));

  return (
    <div className="space-y-6" data-testid="anwaelte-tab">
      {/* ── Abrechnungs-Zähler ── */}
      <Card className="p-5" data-testid="anwaelte-stats-card">
        <div className="flex items-center gap-2 mb-1">
          <Scale className="h-4 w-4 text-cyan-700" />
          <h3 className="font-display font-bold text-base">Fälle pro Kanzlei</h3>
        </div>
        <p className="text-xs text-zinc-500 mb-4">
          Zähler für die Abrechnung — wie viele Akten wurden welchem Anwalt per Vollmacht zugewiesen.
        </p>
        {stats.length === 0 ? (
          <div className="text-sm text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-6 text-center" data-testid="anwaelte-stats-empty">
            Noch keine Akten an Anwälte übergeben. Die Zuweisung erfolgt in der Akte unter
            {" "}<strong>Abtretung → Vollmacht Anwalt</strong>.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="anwaelte-stats-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-400 border-b border-zinc-100">
                  <th className="py-2 pr-4">Kanzlei / Anwalt</th>
                  <th className="py-2 pr-4 text-right">Fälle gesamt</th>
                  <th className="py-2 pr-4 text-right">Vollmacht signiert</th>
                  <th className="py-2 text-right">Zuletzt zugewiesen</th>
                </tr>
              </thead>
              <tbody>
                {stats.map((s) => (
                  <tr key={s.lawyer_id} className="border-b border-zinc-50" data-testid={`anwalt-stat-row-${s.lawyer_id}`}>
                    <td className="py-2.5 pr-4">
                      <div className="font-semibold text-zinc-900">{s.firm || s.name}</div>
                      {s.firm && s.name && <div className="text-xs text-zinc-500">{s.name}{s.city ? ` · ${s.city}` : ""}</div>}
                    </td>
                    <td className="py-2.5 pr-4 text-right">
                      <Badge className="bg-cyan-100 text-cyan-800 border-0 font-bold" data-testid={`anwalt-stat-total-${s.lawyer_id}`}>{s.total}</Badge>
                    </td>
                    <td className="py-2.5 pr-4 text-right text-emerald-700 font-semibold">{s.signed}</td>
                    <td className="py-2.5 text-right text-xs text-zinc-500">{fmtDate(s.last_assigned)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ── Stammdaten-Verwaltung ── */}
      <Card className="p-5" data-testid="anwaelte-manage-card">
        <h3 className="font-display font-bold text-base mb-1">
          {editingId ? "Anwalt bearbeiten" : "Neuen Anwalt anlegen"}
        </h3>
        <p className="text-xs text-zinc-500 mb-4">
          Diese Kanzleien stehen den Gutachtern in der Akte als feste Auswahl zur Verfügung.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Ansprechpartner / Anwalt *</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="RA Max Mustermann" data-testid="lawyer-form-name" />
          </div>
          <div>
            <Label className="text-xs">Kanzlei</Label>
            <Input value={form.firm} onChange={(e) => set("firm", e.target.value)} placeholder="Kanzlei Mustermann & Partner" data-testid="lawyer-form-firm" />
          </div>
          <div>
            <Label className="text-xs">Straße & Hausnummer</Label>
            <Input value={form.address_line} onChange={(e) => set("address_line", e.target.value)} data-testid="lawyer-form-address" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">PLZ</Label>
              <Input value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} data-testid="lawyer-form-plz" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Stadt</Label>
              <Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="lawyer-form-city" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Telefon</Label>
            <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="lawyer-form-phone" />
          </div>
          <div>
            <Label className="text-xs">E-Mail</Label>
            <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="lawyer-form-email" />
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <Button onClick={submit} disabled={busy} data-testid="lawyer-form-submit">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {editingId ? "Speichern" : "Anlegen"}
          </Button>
          {editingId && (
            <Button variant="outline" onClick={() => { setEditingId(null); setForm(emptyLawyer()); }} data-testid="lawyer-form-cancel">
              Abbrechen
            </Button>
          )}
        </div>

        {/* Liste */}
        <div className="mt-6 space-y-2" data-testid="anwaelte-list">
          {lawyers.length === 0 && (
            <div className="text-sm text-zinc-400 text-center py-4">Noch keine Anwälte hinterlegt.</div>
          )}
          {lawyers.map((l) => {
            const st = statById[l.id];
            return (
              <div key={l.id} className="flex items-center gap-3 rounded-xl border border-zinc-200 px-4 py-3" data-testid={`lawyer-row-${l.id}`}>
                <div className="h-9 w-9 rounded-lg bg-cyan-50 text-cyan-700 flex items-center justify-center shrink-0">
                  <Scale className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-zinc-900 truncate">{l.firm || l.name}</div>
                  <div className="text-xs text-zinc-500 truncate">
                    {[l.firm ? l.name : null, [l.postal_code, l.city].filter(Boolean).join(" "), l.email].filter(Boolean).join(" · ")}
                  </div>
                </div>
                {st && (
                  <Badge variant="outline" className="bg-cyan-50 text-cyan-700 border-cyan-200 shrink-0">
                    {st.total} {st.total === 1 ? "Fall" : "Fälle"}
                  </Badge>
                )}
                <Button variant="ghost" size="sm" onClick={() => startEdit(l)} data-testid={`lawyer-edit-${l.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50" onClick={() => remove(l.id)} data-testid={`lawyer-delete-${l.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

/* ============ PROVISIONEN (Partner-Auszahlungen) ============ */
function ProvisionenTab() {
  const [stats, setStats] = useState([]);
  const [dueOrders, setDueOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: o }] = await Promise.all([
        api.get("/partner-orders/stats"),
        api.get("/partner-orders", { params: { status: "report_done" } }),
      ]);
      setStats(s.stats || []);
      setDueOrders(o.orders || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const markPaid = async (order) => {
    if (!window.confirm(`${fmtEuro(order.commission_amount)} an ${order.partner?.name || "Partner"} als ausgezahlt markieren?\n\nNur nach Zahlungseingang der Versicherung!`)) return;
    setBusyId(order.id);
    try {
      await api.post(`/partner-orders/${order.id}/mark-paid`);
      toast.success("Provision als ausgezahlt markiert");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="space-y-6" data-testid="provisionen-tab">
      {/* ── Fällige Auszahlungen ── */}
      <Card className="p-5" data-testid="provisionen-due-card">
        <div className="flex items-center gap-2 mb-1">
          <Wallet className="h-4 w-4 text-emerald-700" />
          <h3 className="font-display font-bold text-base">Fällige Auszahlungen</h3>
          {dueOrders.length > 0 && <Badge className="bg-amber-100 text-amber-800 border-0">{dueOrders.length}</Badge>}
        </div>
        <p className="text-xs text-zinc-500 mb-4">
          Gutachten fertig — Provision auszahlen, sobald die Versicherung gezahlt hat. Danach hier als ausgezahlt markieren.
        </p>
        {dueOrders.length === 0 ? (
          <div className="text-sm text-zinc-400 bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-5 text-center" data-testid="provisionen-due-empty">
            Keine fälligen Auszahlungen.
          </div>
        ) : (
          <div className="space-y-2">
            {dueOrders.map((o) => (
              <div key={o.id} className="flex items-center gap-3 rounded-xl border border-zinc-200 px-4 py-3 flex-wrap" data-testid={`provision-due-${o.id}`}>
                <div className="flex-1 min-w-[180px]">
                  <div className="font-semibold text-sm text-zinc-900">
                    {[o.customer_firstname, o.customer_name].filter(Boolean).join(" ")}
                    {o.license_plate ? <span className="text-zinc-400 font-normal"> · {o.license_plate}</span> : null}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    Partner: <strong>{o.partner?.name || "—"}</strong>
                    {o.partner?.payout_iban ? <span className="font-mono"> · {o.partner.payout_iban}</span> : <span className="text-amber-600"> · keine IBAN hinterlegt</span>}
                  </div>
                </div>
                <div className="text-sm font-bold tabular-nums">{fmtEuro(o.commission_amount)}</div>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  onClick={() => markPaid(o)}
                  disabled={busyId === o.id}
                  data-testid={`provision-markpaid-${o.id}`}
                >
                  {busyId === o.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
                  Ausgezahlt
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ── Zähler pro Partner ── */}
      <Card className="p-5" data-testid="provisionen-stats-card">
        <h3 className="font-display font-bold text-base mb-1">Übersicht pro Partner</h3>
        <p className="text-xs text-zinc-500 mb-4">Alle Provisionspartner mit Aufträgen, offenen und ausgezahlten Provisionen.</p>
        {stats.length === 0 ? (
          <div className="text-sm text-zinc-400 text-center py-4" data-testid="provisionen-stats-empty">Noch keine Partner-Aufträge.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="provisionen-stats-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-400 border-b border-zinc-100">
                  <th className="py-2 pr-4">Partner</th>
                  <th className="py-2 pr-4 text-right">Aufträge</th>
                  <th className="py-2 pr-4 text-right">Offen</th>
                  <th className="py-2 pr-4 text-right">Ausgezahlt</th>
                  <th className="py-2 text-right">Letzter Auftrag</th>
                </tr>
              </thead>
              <tbody>
                {stats.map((s) => (
                  <tr key={s.partner_id} className="border-b border-zinc-50" data-testid={`provision-stat-${s.partner_id}`}>
                    <td className="py-2.5 pr-4">
                      <div className="font-semibold text-zinc-900">{s.name}</div>
                      <div className="text-xs text-zinc-500">{s.email}{s.payout_iban ? ` · ${s.payout_iban}` : ""}</div>
                    </td>
                    <td className="py-2.5 pr-4 text-right font-semibold tabular-nums">{s.total}</td>
                    <td className="py-2.5 pr-4 text-right text-amber-700 font-semibold tabular-nums">{fmtEuro(s.open_amount)}</td>
                    <td className="py-2.5 pr-4 text-right text-emerald-700 font-semibold tabular-nums">{fmtEuro(s.paid_amount)}</td>
                    <td className="py-2.5 text-right text-xs text-zinc-500">{fmtDate(s.last_order)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
